import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError, useAuth } from '@saas-kit/lib';
import { toast } from '@saas-kit/ui';

import { fetchBranding, updateBranding, uploadBrandingLogo } from './apiBranding';
import type { QmsBrandingUpdate } from './types';

/** Query + mutations for the tenant-wide queue branding tab. */
export function useBranding() {
  const { t } = useTranslation('qms');
  const tenantId = useAuth((s) => s.selectedTenant?.tenant_id);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['qms-branding', tenantId],
    queryFn: fetchBranding,
    enabled: Boolean(tenantId),
  });

  const saveMutation = useMutation({
    mutationFn: (input: QmsBrandingUpdate) => updateBranding(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['qms-branding'] });
      toast.success(t('toasts.updated'));
    },
    onError: (error) => toast.error(parseApiError(error).message),
  });

  const logoMutation = useMutation({
    mutationFn: (file: File) => uploadBrandingLogo(file),
    onError: (error) => toast.error(parseApiError(error).message),
  });

  return {
    branding: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    save: (input: QmsBrandingUpdate) => saveMutation.mutateAsync(input),
    saving: saveMutation.isPending,
    uploadLogo: (file: File) => logoMutation.mutateAsync(file),
    uploadingLogo: logoMutation.isPending,
  };
}
